import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// Types
export type TokenStandard = 'ERC20' | 'ERC1400' | 'ERC4626';
export type AuditStatus = 'PENDING_AUDIT' | 'AUDITED' | 'AUDIT_FAILED';
export type BlockchainNetwork = 
  | 'ETHEREUM' | 'ETHEREUM_SEPOLIA' 
  | 'POLYGON' | 'POLYGON_MUMBAI' 
  | 'BSC' | 'BSC_TESTNET' 
  | 'AVALANCHE' | 'ARBITRUM' | 'OPTIMISM' | 'BASE' | 'DREX';

export interface ParameterSchema {
  name: string;
  type: 'string' | 'uint256' | 'address' | 'bool' | 'bytes32';
  required: boolean;
  defaultValue?: any;
  description: string;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
  };
}

export interface SmartContractTemplate {
  id: string;
  name: string;
  description: string | null;
  tokenStandard: TokenStandard;
  sourceCode: string;
  compiledBytecode: string | null;
  abi: any[] | null;
  codeHash: string | null;
  documentationUrl: string | null;
  parameterSchema: ParameterSchema[];
  auditStatus: AuditStatus;
  auditorCompany: string | null;
  auditDate: string | null;
  auditReportUrl: string | null;
  auditedCodeHash: string | null;
  auditFindings: string | null;
  currentVersion: string;
  activeVersionId: string | null;
  isActive: boolean;
  isPublic: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    deployments: number;
    versions: number;
  };
}

export interface TemplateVersion {
  id: string;
  templateId: string;
  version: string;
  sourceCode: string;
  codeHash: string;
  changelog: string | null;
  auditStatus: AuditStatus;
  auditorCompany: string | null;
  auditDate: string | null;
  createdBy: string;
  createdAt: string;
}

export interface CreateTemplateDto {
  name: string;
  description?: string;
  tokenStandard: TokenStandard;
  sourceCode: string;
  documentationUrl?: string;
  parameterSchema: ParameterSchema[];
}

export interface UpdateTemplateDto {
  name?: string;
  description?: string;
  sourceCode?: string;
  documentationUrl?: string;
  parameterSchema?: ParameterSchema[];
  isActive?: boolean;
  isPublic?: boolean;
}

export interface ListTemplatesQuery {
  tokenStandard?: TokenStandard;
  auditStatus?: AuditStatus;
  isActive?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedTemplates {
  data: SmartContractTemplate[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// API Functions
const fetchTemplates = async (query: ListTemplatesQuery = {}): Promise<PaginatedTemplates> => {
  const params = new URLSearchParams();
  if (query.tokenStandard) params.append('tokenStandard', query.tokenStandard);
  if (query.auditStatus) params.append('auditStatus', query.auditStatus);
  if (query.isActive !== undefined) params.append('isActive', String(query.isActive));
  if (query.search) params.append('search', query.search);
  if (query.page) params.append('page', String(query.page));
  if (query.limit) params.append('limit', String(query.limit));
  
  const response = await api.get(`/tokenization/smart-contracts/templates?${params.toString()}`);
  return response.data;
};

const fetchTemplate = async (id: string): Promise<SmartContractTemplate> => {
  const response = await api.get(`/tokenization/smart-contracts/templates/${id}`);
  return response.data;
};

const createTemplate = async (dto: CreateTemplateDto): Promise<SmartContractTemplate> => {
  const response = await api.post('/tokenization/smart-contracts/templates', dto);
  return response.data;
};

const updateTemplate = async ({ id, dto }: { id: string; dto: UpdateTemplateDto }): Promise<SmartContractTemplate> => {
  const response = await api.put(`/tokenization/smart-contracts/templates/${id}`, dto);
  return response.data;
};

const deleteTemplate = async (id: string): Promise<void> => {
  await api.delete(`/tokenization/smart-contracts/templates/${id}`);
};

const fetchTemplateCode = async (id: string): Promise<string> => {
  const response = await api.get(`/tokenization/smart-contracts/templates/${id}/code`);
  return response.data.code;
};

const fetchTemplateVersions = async (templateId: string): Promise<TemplateVersion[]> => {
  const response = await api.get(`/tokenization/smart-contracts/templates/${templateId}/versions`);
  return response.data;
};

const fetchVersionDiff = async (templateId: string, v1Id: string, v2Id: string): Promise<any> => {
  const response = await api.get(`/tokenization/smart-contracts/templates/${templateId}/diff?v1=${v1Id}&v2=${v2Id}`);
  return response.data;
};

// Hooks
export function useTemplates(query: ListTemplatesQuery = {}) {
  return useQuery({
    queryKey: ['smart-contract-templates', query],
    queryFn: () => fetchTemplates(query),
  });
}

export function useTemplate(id: string | undefined) {
  return useQuery({
    queryKey: ['smart-contract-template', id],
    queryFn: () => fetchTemplate(id!),
    enabled: !!id,
  });
}

export function useTemplateCode(id: string | undefined) {
  return useQuery({
    queryKey: ['smart-contract-template-code', id],
    queryFn: () => fetchTemplateCode(id!),
    enabled: !!id,
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smart-contract-templates'] });
    },
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: updateTemplate,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['smart-contract-templates'] });
      queryClient.invalidateQueries({ queryKey: ['smart-contract-template', data.id] });
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smart-contract-templates'] });
    },
  });
}

export function useTemplateVersions(templateId: string | undefined) {
  return useQuery({
    queryKey: ['smart-contract-template-versions', templateId],
    queryFn: () => fetchTemplateVersions(templateId!),
    enabled: !!templateId,
  });
}

export function useVersionDiff(templateId: string | undefined, v1Id: string, v2Id: string) {
  return useQuery({
    queryKey: ['smart-contract-version-diff', templateId, v1Id, v2Id],
    queryFn: () => fetchVersionDiff(templateId!, v1Id, v2Id),
    enabled: !!templateId && !!v1Id && !!v2Id,
  });
}

// Export all hooks as a single object for convenience
export const useSmartContractTemplates = {
  useTemplates,
  useTemplate,
  useTemplateCode,
  useTemplateVersions,
  useVersionDiff,
  useCreateTemplate,
  useUpdateTemplate,
  useDeleteTemplate,
};

export default useSmartContractTemplates;
